import useInView from '../hooks/useInView';
import { useLanguage } from '../context/LanguageContext';

const BOTTLES = {
  tamyanka:       'https://customer-assets.emergentagent.com/job_bohemian-whites/artifacts/02rwozjt_Tamyanka%20with%20label.png',
  pinotGris:      'https://customer-assets.emergentagent.com/job_bohemian-whites/artifacts/vvfbpnc8_Pinot%20gris%20with%20label.png',
  sauvignonBlanc: 'https://customer-assets.emergentagent.com/job_bohemian-whites/artifacts/swdlitnz_Sauvignon%20Blanc%20with%20label.png',
  chardonnay:     'https://customer-assets.emergentagent.com/job_bohemian-whites/artifacts/a763dadn_Chardonnay%20with%20label.png',
};

const BOTTLE_IMAGES = [BOTTLES.tamyanka, BOTTLES.pinotGris, BOTTLES.sauvignonBlanc, BOTTLES.chardonnay];
const WINE_IDS = ['muscat-bianco', 'pinot-gris', 'sauvignon-blanc', 'chardonnay'];

const WineCard = ({ wine, delay, inView }) => (
  <div
    data-testid={`wine-card-${wine.id}`}
    className={`wine-card reveal ${delay} ${inView ? 'visible' : ''}
      flex flex-col group cursor-default
      border border-winery-border hover:border-winery-gold transition-colors duration-500`}
  >
    <div className="relative overflow-hidden bg-cream" style={{ aspectRatio: '1 / 1' }}>
      <img
        src={wine.image}
        alt={`${wine.variety} — Geranium Bohemicum`}
        className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
        loading="lazy"
      />
    </div>

    <div className="flex flex-col flex-1 p-7 md:p-8 bg-cream">
      <p className="wine-numeral font-heading text-3xl font-light text-winery-border group-hover:text-winery-gold transition-colors duration-400 mb-5">
        {wine.numeral}
      </p>
      <h3 className="font-heading font-light text-obsidian mb-1" style={{ fontSize: '1.4rem' }}>
        {wine.variety}
      </h3>
      <p className="font-script text-lg text-winery-text mb-5">{wine.local}</p>
      <div className="line-gold mb-5" style={{ margin: '0 0 1.25rem 0' }} />
      <p className="font-body text-sm text-winery-text leading-relaxed mb-6 flex-1">
        {wine.description}
      </p>
      <p className="font-body text-xs tracking-[0.2em] uppercase text-winery-gold/80">
        {wine.notes}
      </p>
    </div>
  </div>
);

const Wines = () => {
  const [ref, inView] = useInView(0.06);
  const { t } = useLanguage();

  const wines = t.wines.wines.map((w, i) => ({
    ...w,
    id: WINE_IDS[i],
    image: BOTTLE_IMAGES[i],
  }));

  return (
    <section id="wines" data-testid="wines-section" className="bg-winery-muted py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>

        <div className="max-w-xl mb-16 md:mb-20">
          <div className={`reveal ${inView ? 'visible' : ''}`}>
            <span className="section-label">{t.wines.label}</span>
            <div className="line-gold mt-4 mb-8" />
          </div>
          <h2
            className={`reveal reveal-delay-1 font-heading font-light text-obsidian leading-tight mb-5 ${inView ? 'visible' : ''}`}
            style={{ fontSize: 'clamp(2rem, 4vw, 3.2rem)' }}
          >
            {t.wines.heading[0]}<br />{t.wines.heading[1]}
          </h2>
          <p className={`reveal reveal-delay-2 font-body text-winery-text text-base leading-relaxed ${inView ? 'visible' : ''}`}>
            {t.wines.intro}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {wines.map((wine, i) => (
            <WineCard
              key={wine.id}
              wine={wine}
              delay={`reveal-delay-${i + 1}`}
              inView={inView}
            />
          ))}
        </div>

        <div className={`reveal reveal-delay-4 mt-14 text-center ${inView ? 'visible' : ''}`}>
          <a
            href="#contact"
            data-testid="wines-cta-contact"
            className="font-body text-xs tracking-widest uppercase px-10 py-4 border border-obsidian text-obsidian hover:bg-obsidian hover:text-cream transition-all duration-300 inline-block"
          >
            {t.wines.cta}
          </a>
        </div>
      </div>
    </section>
  );
};

export default Wines;
